///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Model, File, Assembly } from "@inweb/client";
import { Viewer } from "../Viewer";

export const composeMatrixFromTransform = (transform, modelCenter, visLib) => {
  const { translate, scale, rotation } = transform;

  const translateMatrix = new visLib.Matrix3d();
  translateMatrix.setTranslation([translate.x, translate.y, translate.z]);

  const rotateMatrix = new visLib.Matrix3d();
  rotateMatrix.setToRotation(rotation.angle, [rotation.x, rotation.y, rotation.z], modelCenter);

  const scaleMatrix = new visLib.Matrix3d();
  scaleMatrix.setToScaling(scale, modelCenter);

  return rotateMatrix.postMultBy(translateMatrix).postMultBy(scaleMatrix);
};

export function applyModelTransform(viewer: Viewer, model: Model | File | Assembly) {
  if (!viewer.visualizeJs) return;
  if (!model.getModelTransformMatrix) return; // Model.getModelTransformMatrix() since 24.3.14

  const visLib = viewer.visLib();
  const visViewer = visLib.getViewer();

  const modelItr = visViewer.getModelIterator();
  for (; !modelItr.done(); modelItr.step()) {
    const modelPtr = modelItr.getModel();

    const transform = model.getModelTransformMatrix(modelPtr.getDatabaseHandle());
    if (transform) {
      const extents = modelPtr.getExtents();
      extents.transformBy(modelPtr.getUnitsMatrix());

      const matrix = composeMatrixFromTransform(transform, extents.center(), visLib);

      modelPtr.setModelingMatrix(matrix, true);
    }
  }
  modelItr.delete();

  visViewer.clearViewExtentsCache?.();

  viewer.update();
}
